import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, FormControlLabel, Checkbox, FormControl } from "@mui/material";
import dayjs from "dayjs";

const ViewOfferForm = ({
  rfqNo,
  offerData,
}) => {
  const initialFormData = {
    BankName: "",
    BankID: "",
    Instrument: "",
    InterestRateMargin: "",
    IssuanceCharges: "",
    ConfirmationCharges: "",
    TenorOffered: "",
    Currency: "",
    TotalCost: "",
    ConditionSummary: "",
    OfferValidity: "",
    requiredDocuments: {
      commercialInvoice: false,
      billOfLading: false,
      certificateOfAnalysis: false,
      packingList: false,
      certificateOfOrigin: false,
      insurancePolicy: false,
      inspectionCertificate: false,
    },
  };

  const [formData, setFormData] = useState(initialFormData);

  // Populate form with offerData
  useEffect(() => {
    if (offerData) {
      const requiredDocs = offerData.RequiredDocuments
        ? offerData.RequiredDocuments.split(", ").reduce((acc, doc) => {
            const keyMap = {
              "Commercial Invoice": "commercialInvoice",
              "Bill of Lading": "billOfLading",
              "Certificate of Analysis": "certificateOfAnalysis",
              "Packing List": "packingList",
              "Certificate of Origin": "certificateOfOrigin",
              "Insurance Policy": "insurancePolicy",
              "Inspection Certificate": "inspectionCertificate",
            };
            const key = keyMap[doc];
            if (key) acc[key] = true;
            return acc;
          }, { ...initialFormData.requiredDocuments })
        : { ...initialFormData.requiredDocuments };

      setFormData({
        BankName: offerData.BankName || "",
        BankID: offerData.BankID || "",
        Instrument: offerData.Instrument || "",
        InterestRateMargin: offerData.InterestRateMargin || "",
        IssuanceCharges: offerData.IssuanceCharges || "",
        ConfirmationCharges: offerData.ConfirmationCharges || "",
        TenorOffered: offerData.TenorOffered || "",
        Currency: offerData.Currency || "",
        TotalCost: offerData.TotalCost || "",
        ConditionSummary: offerData.ConditionSummary || "",
        OfferValidity: offerData.OfferValidity
          ? dayjs(offerData.OfferValidity).format("YYYY-MM-DD")
          : "",
        requiredDocuments: requiredDocs,
      });
    }
  }, [offerData]);

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "#de6b14",
          pb: 1,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#de6b14", fontWeight: "bold" }}>
          View Offer: {formData.BankName}
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Bank Name
            </Typography>
            <TextField
              fullWidth
              name="BankName"
              value={formData.BankName}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4" style={{ display: "none" }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Bank ID
            </Typography>
            <TextField
              fullWidth
              name="BankID"
              value={formData.BankID}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
              type="hidden"
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Instrument
            </Typography>
            <TextField
              fullWidth
              name="Instrument"
              value={formData.Instrument}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Interest Rate Margin
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="InterestRateMargin"
              value={formData.InterestRateMargin}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Issuance Charges
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="IssuanceCharges"
              value={formData.IssuanceCharges}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Confirmation Charges
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="ConfirmationCharges"
              value={formData.ConfirmationCharges}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Tenor Offered
            </Typography>
            <TextField
              fullWidth
              name="TenorOffered"
              value={formData.TenorOffered}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Currency
            </Typography>
            <TextField
              fullWidth
              name="Currency"
              value={formData.Currency}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Total Cost
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="TotalCost"
              value={formData.TotalCost}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Condition Summary
            </Typography>
            <TextField
              fullWidth
              name="ConditionSummary"
              value={formData.ConditionSummary}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-12 col-md-4">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Offer Validity
            </Typography>
            <TextField
              fullWidth
              type="date"
              name="OfferValidity"
              value={formData.OfferValidity}
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-8">
            <FormControl fullWidth>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Required Documents
              </Typography>
              <div className="d-flex flex-wrap gap-3">
                {[
                  {
                    label: "Commercial Invoice",
                    name: "commercialInvoice",
                  },
                  { label: "Bill of Lading (BL)", name: "billOfLading" },
                  {
                    label: "Certificate of Analysis (COA)",
                    name: "certificateOfAnalysis",
                  },
                  { label: "Packing List", name: "packingList" },
                  {
                    label: "Certificate of Origin",
                    name: "certificateOfOrigin",
                  },
                  { label: "Insurance Policy", name: "insurancePolicy" },
                  {
                    label: "Inspection Certificate",
                    name: "inspectionCertificate",
                  },
                ].map((doc) => (
                  <FormControlLabel
                    key={doc.name}
                    control={
                      <Checkbox
                        name={doc.name}
                        checked={formData.requiredDocuments[doc.name]}
                        size="small"
                        sx={{
                          color: "#de6b14",
                          "&.Mui-checked": {
                            color: "#de6b14",
                          },
                        }}
                        disabled
                      />
                    }
                    label={doc.label}
                  />
                ))}
              </div>
            </FormControl>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default ViewOfferForm;